import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *        COPYRIGHT 2019 - 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbDropdownIcon } from "abreact/incubator/ui/component/AbBaseDropdown";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbFilteredList } from "abreact/input/AbFilteredList";
import { AbFilteredTree } from "abreact/input/AbFilteredTree";
import { AbList } from "abreact/input/AbList";
import { AbPlaceholder } from "abreact/input/AbPlaceholder";
import { AbPopupInput } from "abreact/input/AbPopupInput";
import { AbMenuContext } from "abreact/menu/AbMenu";
import { AbButtonIconSide } from "abreact/misc/AbButton";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { AbTree } from "abreact/tree/AbTree";
import { AbTreeUtils } from "abreact/tree/AbTreeUtils";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { KeyValue, SINGLE_CHARACTER } from "abweb/utils/KeyboardUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";

/**
 * The properties for an AbDropdown.
 */

/**
 * A React-based implementation of a dropdown component.
 */
export function AbDropdown(props) {
  const uniqueId = AbHooks.useUniqueId("ab-dropdown");
  const popupButtonRef = React.useRef(null);
  const menuContext = React.useContext(AbMenuContext);
  const inMenu = menuContext.path !== undefined || menuContext.closeEntireMenu !== undefined;
  AbHooks.useAutoSelect({
    ...props,
    items: props.items.map(item => AbEnriched.getItem(item))
  });
  const renderValue = () => {
    if (props.value === undefined) {
      return /*#__PURE__*/React.createElement(AbPlaceholder, {
        placeholder: props.placeholder
      });
    }
    // Note: cast to any as the inferred type still includes undefined
    return (props.valueRenderer ?? props.itemRenderer ?? AbItemUtils.defaultItemDisplayName)(props.value);
  };
  const onArrow = () => {
    popupButtonRef.current?.open();
  };
  const keyHandler = KeyboardUtilsReact.makeKeyHandler([[[KeyValue.ARROW_DOWN, KeyValue.ARROW_UP], onArrow]]);
  const [listInitialCharToFocus, setListInitialCharToFocus] = React.useState();
  const onKeyDown = e => {
    if (SINGLE_CHARACTER.test(e.key)) {
      popupButtonRef.current?.open();
      setListInitialCharToFocus(e.key);
    }
    keyHandler(e);
  };
  const {
    items,
    value,
    placeholder,
    onChange,
    itemRenderer,
    valueRenderer,
    popupParentClass,
    onSelectionChange,
    keyFunction,
    className,
    ...rest
  } = props;
  const popupContents = /*#__PURE__*/React.createElement("div", {
    className: "ab-dropdown-content",
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement(AbList, {
    items: items.map(item => {
      if (item === AB_ITEM_SEPARATOR) {
        return item;
      }
      const enrichment = AbEnriched.getEnrichment(item);
      return AbEnriched.create(AbEnriched.getItem(item), {
        ...enrichment,
        className: ReactUtils.classNamesString([enrichment?.className, "ab-dropdown-list-item"])
      });
    }),
    virtualized: false,
    itemRenderer: itemRenderer,
    onFocusChange: onSelectionChange,
    selectionMode: SelectionMode.SINGLE,
    itemEventHandlers: {
      // These key handlers are so when you click an already selected item, you still close the popup
      onClick: item => {
        if (TypeUtils.equals(props.value, item, props.keyFunction)) {
          popupButtonRef.current?.close();
        }
      },
      onKeyDown: (item, e) => {
        if ([KeyValue.SPACE, KeyValue.ENTER].includes(e.key) && TypeUtils.equals(props.value, item, props.keyFunction)) {
          popupButtonRef.current?.close();
        }
      }
    },
    onChange: item => {
      onChange(item);
      popupButtonRef.current?.close();
    },
    value: value,
    keyFunction: keyFunction,
    __typeToFocusProps: {
      initialFocusString: listInitialCharToFocus
    }
  }));
  const label = /*#__PURE__*/React.createElement("div", {
    className: "value"
  }, renderValue());
  return /*#__PURE__*/React.createElement(AbPopupButton, _extends({
    className: ReactUtils.classNamesString(["ab-dropdown", "ab-single-line", "ab-shrinkable-button", className])
  }, rest, {
    id: props.id ?? uniqueId,
    abRef: popupButtonRef,
    tooltipProps: {
      clippedTooltip: label,
      clippedTrigger: {
        get current() {
          if (!popupButtonRef.current) return null;
          if (props.value === undefined || props.tooltipClippedSelector) {
            return ReactUtils.tryQueries(popupButtonRef.current.buttonElement, props.tooltipClippedSelector, ".ab-placeholder");
          }
          return ReactUtils.tryQueries(popupButtonRef.current.buttonElement, ".ab-item-label", ".value");
        }
      }
    },
    label: label,
    icon: /*#__PURE__*/React.createElement(AbDropdownIcon, {
      popupPosition: inMenu ? PopupPosition.RIGHT : PopupPosition.BELOW
    }),
    iconSide: AbButtonIconSide.RIGHT,
    popupContents: popupContents,
    popupProps: {
      positioner: inMenu ? PopupUtils.HORIZONTAL_POSITIONER : PopupUtils.VERTICAL_POSITIONER,
      className: StringUtils.cssClassStr("ab-dropdown-popup", popupParentClass),
      dataTestId: props.dataTestId ? props.dataTestId + "_popup" : undefined
    },
    eventHandlers: {
      onKeyDown: props.disabled ? undefined : onKeyDown
    },
    __htmlProps: {
      role: "combobox",
      "data-test-component": "ab-dropdown"
    }
  }));
}
(function (_AbDropdown) {
  const SEPARATOR = _AbDropdown.SEPARATOR = AB_ITEM_SEPARATOR;
  /**
   * When a character is pressed with the dropdown closed and focused, once the popup opens, either: focus on a matching
   * item (if non-filtered), or type the character into the filter box (if filtered).
   */
  const useTypeToOpen = _AbDropdown.useTypeToOpen = () => {
    const [keyDownOnDropdown, setKeyDownOnDropdown] = React.useState();
    const onKeyDown = e => {
      if (SINGLE_CHARACTER.test(e.key) && e.key !== KeyValue.SPACE) {
        setKeyDownOnDropdown(e);
      }
    };
    const initialString = keyDownOnDropdown?.key;
    // used to make sure string in filter box is cleared when close & re-open filtered dropdown
    const clearInitialString = () => setKeyDownOnDropdown(undefined);
    return [onKeyDown, initialString, clearInitialString];
  };
  // class names and id for rich dropdowns
  const useRichCommonProps = (props, kind) => {
    const className = ReactUtils.classNamesString(["ab-rich-dropdown", `ab-rich-dropdown-${kind}`, "ab-shrinkable-button", props.className]);
    let id = AbHooks.useUniqueId(`ab-rich-dropdown-${kind}`); // set unconditionally, not used if props.id non-null.
    if (props.id) id = props.id;
    const popupParentClass = ReactUtils.classNamesString(["ab-rich-dropdown-popup", `ab-rich-dropdown-${kind}-popup`, props.popupParentClass, props.selectionMode === SelectionMode.MULTIPLE && "ab-rich-dropdown-checked-popup"]);
    return {
      className,
      id,
      popupParentClass
    };
  };
  function List(props) {
    const {
      valueRenderer,
      listProps,
      id,
      className,
      popupParentClass,
      itemRenderer,
      items,
      ...popupInputProps
    } = props;
    const [onKeyDown, initialString] = AbDropdown.useTypeToOpen();
    return /*#__PURE__*/React.createElement(AbPopupInput, _extends({}, popupInputProps, useRichCommonProps(props, "list"), {
      valueRenderer: props.valueRenderer ?? props.itemRenderer,
      __htmlProps: {
        onKeyDown: onKeyDown
      },
      inputRenderer: selectionListProps => /*#__PURE__*/React.createElement(AbList, _extends({}, selectionListProps, listProps, {
        keyFunction: props.keyFunction,
        items: items,
        itemRenderer: props.itemRenderer,
        __typeToFocusProps: {
          initialFocusString: initialString
        }
      }))
    }));
  }
  _AbDropdown.List = List;
  function FilteredList(props) {
    const {
      positioner,
      valueRenderer,
      filteredListProps,
      id,
      className,
      popupParentClass,
      itemRenderer,
      items,
      filterFunction,
      externalFilter,
      ...popupInputProps
    } = props;
    const [onKeyDown, initialString, clearInitialString] = AbDropdown.useTypeToOpen();
    const wrappedValueRenderer = (value, enrichment) => {
      if (props.valueRenderer) return props.valueRenderer(value, enrichment);
      if (props.itemRenderer) return props.itemRenderer(value, enrichment, {
        filterString: "",
        isMatch: false
      });
      return AbItemUtils.defaultItemDisplayName(value, enrichment);
    };
    return /*#__PURE__*/React.createElement(AbPopupInput, _extends({}, popupInputProps, useRichCommonProps(props, "filtered-list"), {
      positioner: positioner ?? PopupUtils.FILTERED_DROPDOWN_VERTICAL_POSITIONER,
      valueRenderer: wrappedValueRenderer,
      __htmlProps: {
        onKeyDown: onKeyDown
      },
      onClose: clearInitialString,
      inputRenderer: filteredListProps => {
        return /*#__PURE__*/React.createElement(AbFilteredList, _extends({}, filteredListProps, props.filteredListProps, {
          keyFunction: props.keyFunction,
          itemRenderer: props.itemRenderer,
          filterFunction: props.filterFunction,
          externalFilter: props.externalFilter,
          initialFilterString: props.filteredListProps?.initialFilterString ?? initialString,
          items: props.items
        }));
      }
    }));
  }
  _AbDropdown.FilteredList = FilteredList;
  function Tree(props) {
    const {
      valueRenderer,
      treeProps,
      id,
      className,
      popupParentClass,
      itemRenderer,
      roots,
      ...popupInputProps
    } = props;
    const [onKeyDown, initialFocusString] = AbDropdown.useTypeToOpen();

    // if no value renderer provided, assume individual items (not their whole path) should appear as the
    // value on the dropdown button
    const wrappedValueRenderer = (item, enrichment) => {
      if (props.valueRenderer) return props.valueRenderer(item, enrichment);
      if (item.length === 0) return AbItemUtils.defaultItemDisplayName(undefined);
      if (props.itemRenderer) return props.itemRenderer(item[item.length - 1], enrichment, {
        path: item,
        enrichedPath: item
      });
      return AbItemUtils.defaultItemDisplayName(item[item.length - 1], enrichment);
    };
    return /*#__PURE__*/React.createElement(AbPopupInput, _extends({}, popupInputProps, useRichCommonProps(props, "tree"), {
      keyFunction: AbTreeUtils.makePathKeyFunction(props.keyFunction),
      valueRenderer: wrappedValueRenderer,
      __htmlProps: {
        onKeyDown: onKeyDown
      },
      inputRenderer: treeProps => /*#__PURE__*/React.createElement(AbTree, _extends({}, treeProps, props.treeProps, {
        keyFunction: props.keyFunction,
        itemRenderer: props.itemRenderer,
        roots: props.roots,
        __typeToFocusProps: {
          initialFocusString: initialFocusString
        }
      }))
    }));
  }
  _AbDropdown.Tree = Tree;
  const FilteredTree = _AbDropdown.FilteredTree = props => {
    const {
      positioner,
      valueRenderer,
      filteredTreeProps,
      id,
      className,
      popupParentClass,
      itemRenderer,
      roots,
      filterFunction,
      externalFilter,
      ...popupInputProps
    } = props;
    const [onKeyDown, initialString, clearInitialString] = AbDropdown.useTypeToOpen();
    // if no value renderer provided, assume individual items (not their whole path) should appear as the
    // value on the dropdown button
    const wrappedValueRenderer = (item, enrichment) => {
      if (props.valueRenderer) return props.valueRenderer(item, enrichment);
      if (item.length === 0) return AbItemUtils.defaultItemDisplayName(undefined);
      if (props.itemRenderer) return props.itemRenderer(item[item.length - 1], enrichment, {
        path: item,
        isMatch: true,
        filterString: "",
        enrichedPath: item
      });
      return AbItemUtils.defaultItemDisplayName(item[item.length - 1], enrichment);
    };
    return /*#__PURE__*/React.createElement(AbPopupInput, _extends({}, popupInputProps, useRichCommonProps(props, "filtered-tree"), {
      positioner: positioner ?? PopupUtils.FILTERED_DROPDOWN_VERTICAL_POSITIONER,
      keyFunction: AbTreeUtils.makePathKeyFunction(props.keyFunction),
      valueRenderer: wrappedValueRenderer,
      __htmlProps: {
        onKeyDown: onKeyDown
      },
      onClose: clearInitialString,
      placeholder: props.placeholder,
      inputRenderer: treeProps => /*#__PURE__*/React.createElement(AbFilteredTree, _extends({}, treeProps, props.filteredTreeProps, {
        initialFilterString: props.filteredTreeProps?.initialFilterString ?? initialString,
        keyFunction: props.keyFunction,
        itemRenderer: props.itemRenderer,
        filterFunction: props.filterFunction,
        externalFilter: props.externalFilter,
        roots: props.roots
      }))
    }));
  };
})(AbDropdown || (AbDropdown = {}));
//# sourceMappingURL=AbDropdown.js.map
